import React, { useRef, useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  CircularProgress,
  Box,
} from "@mui/material";
import ImportSiteIcon from "../assets/icons/ImportSiteIcon";

const VISIBLE_COUNT = 10;

const ExcelLocationUploader = ({
  scopeOfWork,
  index,
  excelLoading,
  handleIconClickForExcel,
  handleInputChange,
  fileInputForExcelRef,
  handleDeleteExcel,
  columns,
  deleteCheck,
  errors,
}) => {
  const locations = scopeOfWork?.locationsList || [];
  const scrollRef = useRef(null);
  const [startIndex, setStartIndex] = useState(0);
  const [visibleLocations, setVisibleLocations] = useState([]);
  const [isLoadingScroll, setIsLoadingScroll] = useState(false);

  const loadVisibleLocations = (newStartIndex) => {
    // const endIndex = newStartIndex + VISIBLE_COUNT;
    const maxIndex = locations.length - VISIBLE_COUNT;
    const safeStartIndex = Math.max(0, Math.min(newStartIndex, maxIndex));
    const sliced = locations.slice(
      safeStartIndex,
      safeStartIndex + VISIBLE_COUNT
    );
    setVisibleLocations(sliced);
    setStartIndex(safeStartIndex);
  };

  useEffect(() => {
    loadVisibleLocations(0);
  }, [locations]);

  useEffect(() => {
    const scrollEl = scrollRef.current;
    if (!scrollEl) return;

    let prevScrollHeight = scrollEl.scrollHeight;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = scrollEl;

      // Scroll down: Load more items (as before)
      if (
        scrollTop + clientHeight >= scrollHeight - 10 &&
        startIndex + VISIBLE_COUNT < locations.length
      ) {
        setIsLoadingScroll(true);
        setTimeout(() => {
          const newIndex = startIndex + VISIBLE_COUNT;
          loadVisibleLocations(newIndex);
          setIsLoadingScroll(false);
          scrollEl.scrollTop = 1; // Reset scroll to top when loading more items down
        }, 300);
      }

      // Scroll up: Load one row at a time and adjust scroll position
      else if (scrollTop === 0 && startIndex > 0) {
        setIsLoadingScroll(true);

        // Load previous rows incrementally
        setTimeout(() => {
          const newStartIndex = Math.max(0, startIndex - 3);
          loadVisibleLocations(newStartIndex);
          setIsLoadingScroll(false);

          // Wait for the scrollHeight to update after data load
          requestAnimationFrame(() => {
            const newScrollHeight = scrollEl.scrollHeight;

            // Adjust the scroll position smoothly to stay in the middle
            scrollEl.scrollTop =
              newScrollHeight - prevScrollHeight + clientHeight / 2;
            prevScrollHeight = newScrollHeight; // Update prevScrollHeight for next scroll event
          });
        }, 300);
      }
    };

    scrollEl.addEventListener("scroll", handleScroll);
    return () => scrollEl.removeEventListener("scroll", handleScroll);
  }, [startIndex, locations.length]);

  useEffect(() => {
    const scrollEl = scrollRef.current;
    if (scrollEl) scrollEl.scrollTop = 1;
  }, []);

  return (
    <>
      {locations.length === 0 ? (
        <div className="border rounded-lg mb-8 shadow-lg">
          <div className="flex justify-between items-center w-full bg-[#e0edf4] p-2 rounded-t-lg">
            <div className="flex justify-center items-center">
              <h1
                style={{
                  color: errors[`locationsList-${index}`]
                    ? "var(--error-color)"
                    : "inherit",
                }}
              >
                Sites
              </h1>
              <span style={{ color: "var(--error-color)" }}>*</span>
            </div>
            <div className="flex justify-center items-center mb-2">
              {excelLoading ? (
                <CircularProgress size={30} />
              ) : (
                <div>
                  <input
                    type="file"
                    ref={fileInputForExcelRef}
                    name={`locationsList-${index}`}
                    onChange={(e) => handleInputChange(e, index)}
                    style={{ display: "none" }}
                    accept=".xlsx"
                  />
                  <button
                    onClick={handleIconClickForExcel}
                    className="px-2 py-2 w-36 gap-2 items-center justify-center mr-2 flex cursor-pointer text-[var(--secondary-color)] bg-[var(--primary-color)] border-[var(--primary-color)] border rounded-lg"
                  >
                    <ImportSiteIcon />
                    Import Sites
                  </button>
                </div>
              )}
            </div>
          </div>
          <div className="flex justify-center items-center">
            <h3 className="w-full text-center mt-2 p-5 ">
              There is no location added yet
            </h3>
          </div>
        </div>
      ) : (
        <>
          <Grid
            className="bg-[var(--secondary-color)] shadow-md p-4 border rounded-lg my-5"
            spacing={3}
          >
            <div className="flex justify-between items-center">
              <div className="flex flex-col mb-5 lg:flex-row justify-between items-center gap-7">
                <h1 className="font-[600]">{`Locations : ${locations.length}`}</h1>
              </div>
              {!deleteCheck && (
                <button
                  onClick={handleDeleteExcel}
                  className="px-2 py-2 w-36 gap-2 items-center mb-5 justify-center mr-2 flex cursor-pointer text-[var(--secondary-color)] bg-[var(--error-color)] border-[var(--secondary-color)] border rounded-lg"
                >
                  <ImportSiteIcon />
                  Delete Sites
                </button>
              )}
            </div>

            <TableContainer
              ref={scrollRef}
              className="mt-5 rounded-lg custom-scrollbar"
              sx={{
                overflowY: "auto",
                maxWidth: "full",
                margin: "auto",
                maxHeight: 300,
              }}
            >
              <Table>
                <TableHead>
                  <TableRow>
                    {columns?.map((column) => (
                      <TableCell
                        key={column.field}
                        sx={{
                          whiteSpace: "nowrap",
                          fontWeight: "550",
                          position: "sticky",
                          top: 0,
                          backgroundColor: "#e0e0e0",
                          width: column.width,
                          zIndex: 2,
                        }}
                      >
                        <span style={{ lineHeight: "1", width: column.width }}>
                          {column.headerName
                            .toLowerCase()
                            .split(" ")
                            .map(
                              (word) =>
                                word.charAt(0).toUpperCase() + word.slice(1)
                            )
                            .join(" ")}
                        </span>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleLocations.map((location, i) => (
                    <TableRow key={startIndex + i}>
                      <TableCell>{`${startIndex + i + 1}.`}</TableCell>
                      <TableCell>
                        {location?.storeName
                          ? location.storeName.charAt(0).toUpperCase() +
                            location.storeName.slice(1)
                          : "N/A"}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center">
                          <div className="border text-[var(--primary-color)] px-2 py-1 rounded">
                            {location?.storeOwnerName
                              ?.split(" ")
                              .map((ele) => ele.charAt(0).toUpperCase())
                              .join("")}
                          </div>
                          <div className="ml-2">
                            <span className="block font-medium">
                              {location?.storeOwnerName
                                ? location.storeOwnerName
                                    .charAt(0)
                                    .toUpperCase() +
                                  location.storeOwnerName.slice(1)
                                : "N/A"}
                            </span>
                            <span className="text-gray-400 text-xs">
                              {location?.storeOwnerPhoneNumber ?? "NA"}
                            </span>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        {typeof location.address === "string"
                          ? location.address
                          : location.address?.richText
                              ?.map((item) => item.text)
                              .join(" ")}
                      </TableCell>
                      <TableCell>{location?.zipCode}</TableCell>
                      <TableCell>{location?.city}</TableCell>
                      <TableCell>{location?.state}</TableCell>
                      <TableCell>
                        {
                          scopeOfWork?.productDimensions[startIndex + i]
                            ?.productType
                        }
                      </TableCell>
                      <TableCell align="center">
                        {scopeOfWork?.productDimensions[startIndex + i]
                          ?.productWidth || "N/A"}
                      </TableCell>
                      <TableCell align="center">
                        {scopeOfWork?.productDimensions[startIndex + i]
                          ?.productHeight || "N/A"}
                      </TableCell>
                      <TableCell align="center">
                        {scopeOfWork?.productDimensions[startIndex + i]
                          ?.productThickness || "N/A"}
                      </TableCell>
                    </TableRow>
                  ))}
                  {isLoadingScroll && (
                    <TableRow>
                      <TableCell colSpan={columns.length} align="center">
                        <CircularProgress size={24} />
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        </>
      )}
      {errors[`locationsList-${index}`] && (
        <span className="ml-4" style={{ color: "var(--error-color)" }}>
          {errors[`locationsList-${index}`]}
        </span>
      )}
    </>
  );
};

export default ExcelLocationUploader;
